import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  FileText,
  Coins,
  AlertCircle,
  ShoppingCart,
  Receipt,
  Landmark,
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Download,
  Plus,
  CheckCircle2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { StatCard, PageHeader } from '@/cajita/ui';
import { BarChart, LineChart, Donut, Gauge, ProgressBar } from '@/cajita/charts';
import { useDashboard, type DashboardSummary } from './dashboard.api';

const fmt = (n: number, dec = 2): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// Etiquetas y colores para los métodos de pago (Payment.method).
const METODO_LABEL: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  YAPE: 'Yape',
  PLIN: 'Plin',
  TRANSFER: 'Transferencia',
  CHECK: 'Cheque',
  CREDIT_BALANCE: 'Saldo a favor',
};
const METODO_COLOR = ['#6FD3A1', '#7FA8E8', '#A892E8', '#EBB04E', '#F1979A', '#5BC0BE', '#C3D3DC'];

const EMPTY: DashboardSummary = {
  currency: 'PEN',
  ventasMes: 0,
  comprobantesMes: 0,
  aceptacionPct: 0,
  aceptacion: { aceptados: 0, observados: 0, rechazados: 0 },
  ventasPorDia: [],
  porCobrar: 0,
  vencido: 0,
  aging: [],
  comprasMes: 0,
  ordenesCompra: 0,
  gastosMes: 0,
  porPagar: 0,
  saldoCuentas: 0,
  cuentasCount: 0,
  flujoCaja: [],
  cajaAbierta: false,
  cajaEsperada: null,
  cajaMetodos: [],
  topProductos: [],
};

function EmptyHint({ text }: { text: string }): JSX.Element {
  return (
    <div className="col aic gap6" style={{ padding: '28px 12px', textAlign: 'center' }}>
      <span className="t-sm muted">{text}</span>
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const tenantName = user?.memberships.find((m) => m.tenantId === activeTenantId)?.tenantName ?? 'tu negocio';
  const { data } = useDashboard();
  const d = data ?? EMPTY;

  const go = (key: string) => {
    const routes: Record<string, string> = {
      pos: '/pos',
      cxc: '/receivables',
      cxp: '/payables',
      compras: '/purchases',
      gastos: '/expenses',
      bancos: '/treasury',
      caja: '/cash-sessions',
    };
    navigate(routes[key] ?? '/');
  };

  const firstName = (user?.name ?? '').split(' ')[0] || 'Hola';
  const hour = new Date().getHours();
  const saludo = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
  const today = new Date().toLocaleDateString('es-PE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const salesData = d.ventasPorDia.map((x) => ({ d: String(x.dia), v: x.total }));
  const salesTotal = salesData.reduce((a, s) => a + s.v, 0);
  const agingMax = Math.max(...d.aging.map((a) => a.val), 1);
  const agingTotal = d.aging.reduce((a, x) => a + x.val, 0);
  const ac = d.aceptacion;

  // Alertas derivadas de datos reales.
  const alerts: Array<{ tone: string; text: string; meta: string; onClick: () => void }> = [];
  if (d.vencido > 0)
    alerts.push({ tone: '#EE8487', text: `${fmt(d.vencido)} en cobranzas vencidas`, meta: 'Revisa Por cobrar', onClick: () => go('cxc') });
  if (d.porPagar > 0)
    alerts.push({ tone: '#EBB04E', text: `${fmt(d.porPagar)} por pagar a proveedores`, meta: 'Revisa Por pagar', onClick: () => go('cxp') });
  if (!d.cajaAbierta)
    alerts.push({ tone: '#7FA8E8', text: 'No tienes una caja abierta', meta: 'Abrir caja del turno', onClick: () => go('caja') });

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title={`${saludo}, ${firstName} 👋`}
          sub={`Resumen de ${tenantName} · ${today}`}
          actions={
            <>
              <div className="seg">
                <button>Hoy</button>
                <button className="on">Este mes</button>
                <button>Año</button>
              </div>
              <button className="btn btn-secondary">
                <Download size={17} />
                Exportar
              </button>
              <button className="btn btn-primary" onClick={() => go('pos')}>
                <Plus size={17} />
                Nueva venta
              </button>
            </>
          }
        />

        {/* KPI row */}
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <StatCard variant="filled" label="Ventas del mes" value={fmt(d.ventasMes)} icon={<TrendingUp size={17} />} sub="este mes" />
          <StatCard label="Comprobantes" value={String(d.comprobantesMes)} icon={<FileText size={19} />} accent="#3B82F6" sub="emitidos este mes" onClick={() => go('cxc')} />
          <StatCard label="Por cobrar" value={fmt(d.porCobrar)} icon={<Coins size={19} />} accent="#84CC16" sub="documentos abiertos" onClick={() => go('cxc')} />
          <StatCard label="Vencido" value={fmt(d.vencido)} icon={<AlertCircle size={19} />} accent="#EF4444" sub="requiere gestión" onClick={() => go('cxc')} />
        </div>

        {/* second KPI row */}
        <div className="kpi-grid" style={{ marginBottom: 24 }}>
          <StatCard label="Compras del mes" value={fmt(d.comprasMes)} icon={<ShoppingCart size={19} />} accent="#8B5CF6" sub={`${d.ordenesCompra} ${d.ordenesCompra === 1 ? 'orden' : 'órdenes'}`} onClick={() => go('compras')} />
          <StatCard label="Gastos del mes" value={fmt(d.gastosMes)} icon={<Receipt size={19} />} accent="#F97316" sub="este mes" onClick={() => go('gastos')} />
          <StatCard label="Por pagar" value={fmt(d.porPagar)} icon={<CreditCard size={19} />} accent="#F59E0B" sub="a proveedores" onClick={() => go('cxp')} />
          <StatCard label="Saldo en cuentas" value={fmt(d.saldoCuentas)} icon={<Landmark size={19} />} accent="#0EA5E9" sub={`${d.cuentasCount} ${d.cuentasCount === 1 ? 'cuenta' : 'cuentas'}`} onClick={() => go('bancos')} />
        </div>

        {/* main grid */}
        <div className="grid grid-collapse" style={{ gridTemplateColumns: '1.7fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Ventas por día</div>
                <div className="card-sub">Este mes · total {fmt(salesTotal, 0)}</div>
              </div>
            </div>
            <div className="card-pad">
              {salesTotal > 0 ? (
                <BarChart data={salesData} accentIdx={salesData.length - 1} fmtY={(v) => 'S/' + (v / 1000).toFixed(0) + 'k'} />
              ) : (
                <EmptyHint text="Aún no registras ventas este mes." />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Aceptación SUNAT</div>
              <ShieldCheck size={18} style={{ color: 'var(--muted-2)' }} />
            </div>
            <div className="card-pad col aic">
              <Gauge value={d.aceptacionPct} max={100} label={`${d.aceptacionPct}%`} sub="aceptados este mes" color="#6FD3A1" />
              <div className="row gap20" style={{ marginTop: 6 }}>
                {([
                  ['Aceptados', ac.aceptados, '#6FD3A1'],
                  ['Observados', ac.observados, '#F4C56E'],
                  ['Rechazados', ac.rechazados, '#F1979A'],
                ] as const).map(([l, n, c]) => (
                  <div key={l} className="col aic" style={{ gap: 2 }}>
                    <div className="row aic gap6">
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: c }} />
                      <span className="t-h2 tnum">{n}</span>
                    </div>
                    <div className="t-xs muted">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* third grid: flujo de caja + cobranza */}
        <div className="grid grid-collapse" style={{ gridTemplateColumns: '1.7fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Flujo de caja</div>
                <div className="card-sub">Ingresos vs egresos · 6 meses</div>
              </div>
              <div className="row gap16">
                <span className="row aic gap6 t-sm fw6">
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: '#6FD3A1' }} />
                  Ingresos
                </span>
                <span className="row aic gap6 t-sm fw6">
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: '#F1979A' }} />
                  Egresos
                </span>
              </div>
            </div>
            <div className="card-pad">
              {d.flujoCaja.some((f) => f.in > 0 || f.out > 0) ? (
                <LineChart data={d.flujoCaja} keys={['in', 'out']} colors={['#6FD3A1', '#F1979A']} fmtY={(v) => 'S/' + (v / 1000).toFixed(0) + 'k'} />
              ) : (
                <EmptyHint text="Sin movimientos de tesorería todavía." />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Cobranza por antigüedad</div>
              <button className="btn btn-ghost btn-sm" onClick={() => go('cxc')}>
                Ver
                <ArrowRight size={15} />
              </button>
            </div>
            <div className="card-pad col gap14">
              {agingTotal > 0 ? (
                d.aging.map((a) => (
                  <div key={a.label} className="col gap6">
                    <div className="row between t-sm">
                      <span className="fw6">{a.label}</span>
                      <span className="tnum muted">{fmt(a.val, 0)}</span>
                    </div>
                    <ProgressBar value={(a.val / agingMax) * 100} color={a.color} />
                  </div>
                ))
              ) : (
                <EmptyHint text="Sin cuentas por cobrar pendientes." />
              )}
            </div>
          </div>
        </div>

        {/* fourth grid: caja + top productos + alertas */}
        <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-head">
              <div className="card-title">Caja del turno</div>
              {d.cajaAbierta ? (
                <span className="badge badge-green">
                  <span className="bdot" />
                  Abierta
                </span>
              ) : (
                <span className="badge badge-gray">
                  <span className="bdot" />
                  Cerrada
                </span>
              )}
            </div>
            <div className="card-pad">
              {d.cajaAbierta && d.cajaMetodos.length > 0 ? (
                <>
                  <div className="col aic" style={{ marginBottom: 16 }}>
                    <Donut
                      size={150}
                      thickness={22}
                      data={d.cajaMetodos.map((m, i) => ({ label: METODO_LABEL[m.metodo] ?? m.metodo, value: m.monto, color: METODO_COLOR[i % METODO_COLOR.length]! }))}
                      centerLabel={fmt(d.cajaEsperada ?? 0, 0)}
                      centerSub="ventas hoy"
                      fmtCenter={(v) => fmt(v, 0)}
                    />
                  </div>
                  <div className="col gap8">
                    {d.cajaMetodos.map((m, i) => (
                      <div key={m.metodo} className="row between t-sm">
                        <span className="row aic gap8">
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: METODO_COLOR[i % METODO_COLOR.length] }} />
                          {METODO_LABEL[m.metodo] ?? m.metodo}
                        </span>
                        <span className="tnum fw6">{fmt(m.monto)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyHint text={d.cajaAbierta ? 'Caja abierta, sin ventas aún.' : 'No hay una caja abierta.'} />
              )}
              <button className="btn btn-secondary btn-block btn-sm" style={{ marginTop: 14 }} onClick={() => go('caja')}>
                Ir a caja
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Top productos</div>
              <span className="t-xs muted">este mes</span>
            </div>
            <div className="card-pad col gap12">
              {d.topProductos.length > 0 ? (
                d.topProductos.map((p, i) => (
                  <div key={p.name} className="row aic gap12">
                    <span className="muted fw7 tnum" style={{ width: 14, fontSize: 13 }}>
                      {i + 1}
                    </span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="fw6 t-sm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.name}
                      </div>
                      <div className="t-xs muted tnum">{p.qty.toLocaleString('es-PE')} und</div>
                    </div>
                    <div className="tnum fw7 t-sm">{fmt(p.total, 0)}</div>
                  </div>
                ))
              ) : (
                <EmptyHint text="Aún no hay ventas este mes." />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Alertas</div>
              {alerts.length > 0 && <span className="badge badge-red">{alerts.length}</span>}
            </div>
            <div className="card-pad col gap10">
              {alerts.length > 0 ? (
                alerts.map((a, i) => (
                  <div
                    key={i}
                    className="row gap10 ait"
                    style={{ padding: '9px 10px', borderRadius: 11, background: 'var(--canvas)', cursor: 'pointer' }}
                    onClick={a.onClick}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', background: `${a.tone}26`, color: a.tone, flex: 'none' }}>
                      <AlertCircle size={16} />
                    </div>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="fw6 t-sm" style={{ lineHeight: 1.3 }}>
                        {a.text}
                      </div>
                      <div className="t-xs muted">{a.meta}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col aic gap6" style={{ padding: '28px 12px', textAlign: 'center' }}>
                  <CheckCircle2 size={26} style={{ color: '#6FD3A1' }} />
                  <span className="t-sm muted">Todo en orden.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
